/**
 * Doro Header — Mobile Navigation & Search
 */

window.theme = window.theme || {};

/* Delegate event helper */
theme.addDelegateEventListener = (root, eventName, selector, handler, opts = null) => {
  const wrapper = (evt) => {
    const match = evt.target.closest(selector);
    if (!match || !root.contains(match)) return;
    handler.call(match, evt, match);
  };
  root.addEventListener(eventName, wrapper, opts);
  return wrapper;
};

/* Suffix IDs for a11y uniqueness */
theme.suffixIds = (container, suffix) => {
  ['id', 'for', 'aria-describedby', 'aria-controls'].forEach((attr) => {
    container.querySelectorAll(`[${attr}]`).forEach((el) =>
      el.setAttribute(attr, el.getAttribute(attr) + suffix)
    );
  });
};

/* Load lazy images manually */
theme.manuallyLoadImages = (container) => {
  if (!container) return;
  container.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    if (img.dataset.src) img.src = img.dataset.src;
  });
};

/* Show mobile nav */
theme.openMobileNav = () => {
  document.body.classList.add('enable-mobile-nav-transition');
  setTimeout(() => {
    document.body.classList.add('reveal-mobile-nav');

    const firstLink = document.querySelector('.mobile-drawer .site-nav-list > .site-nav-entry > .site-nav-anchor');
    if (firstLink) {
      const cs = getComputedStyle(firstLink);
      const wait = parseFloat(cs.transitionDelay.split(',')[0] || 0) + parseFloat(cs.transitionDuration.split(',')[0] || 0);
      setTimeout(() => document.body.classList.add('reveal-mobile-nav--revealed'), wait * 1000);
    } else {
      document.body.classList.add('reveal-mobile-nav--revealed');
    }

    theme.manuallyLoadImages(document.querySelector('.mobile-drawer'));
  }, 10);
};

/* Hide mobile nav */
theme.closeMobileNav = () => {
  document.body.classList.remove('reveal-mobile-nav', 'reveal-mobile-nav--revealed');
  setTimeout(() => document.body.classList.remove('enable-mobile-nav-transition'), 750);
};

/* ---------------------------------------------------- */
/*  StoreHeader custom element                          */
/* ---------------------------------------------------- */
class StoreHeader extends HTMLElement {
  connectedCallback() {
    this._buildMobileDrawer();
    this._bindSearch();
  }

  _buildMobileDrawer() {
    const tpl = this.querySelector('.mobile-drawer-tpl');
    if (!tpl) return;

    const wrap = document.createElement('div');
    wrap.innerHTML = tpl.innerHTML;
    this.drawer = wrap.firstElementChild;

    theme.suffixIds(this.drawer, 'MobileCtx');

    const headerSection = document.querySelector('.section-header');
    if (headerSection) headerSection.insertAdjacentElement('afterend', this.drawer);

    /* Open sub-level */
    theme.addDelegateEventListener(this.drawer, 'click', '.site-nav-list > .site-nav-entry > .site-nav-expand-btn', (evt, btn) => {
      evt.preventDefault();
      btn.parentElement.classList.add('site-nav-entry--open');
      this.drawer.classList.add('mobile-drawer--child-open');

      const heading = this.drawer.querySelector('.mobile-menu-title');
      if (heading && btn.previousElementSibling) heading.innerText = btn.previousElementSibling.innerText;

      const topBar = this.drawer.querySelector('.mobile-drawer__top-bar');
      if (topBar && btn.nextElementSibling) btn.nextElementSibling.style.top = `${Math.ceil(topBar.clientHeight + 1)}px`;

      this.drawer.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });

    /* Parent link → expand if it has children */
    theme.addDelegateEventListener(this.drawer, 'click', '.site-nav-entry--parent > .site-nav-anchor[href="#"]', (evt, el) => {
      evt.preventDefault();
      if (el.nextElementSibling) el.nextElementSibling.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
    });

    /* Back button */
    theme.addDelegateEventListener(this.drawer, 'click', '.mobile-back-btn', (evt) => {
      evt.preventDefault();
      this.drawer.classList.remove('mobile-drawer--child-open');
      this.drawer.querySelectorAll('.site-nav-list > .site-nav-entry--open').forEach((el) => el.classList.remove('site-nav-entry--open'));
    });

    /* Third-level toggle */
    theme.addDelegateEventListener(this.drawer, 'click', '.site-nav-sublevel > .site-nav-entry > .site-nav-expand-btn', (evt, btn) => {
      evt.preventDefault();
      const opening = !btn.parentElement.classList.contains('site-nav-entry--open');
      if (opening) {
        btn.parentElement.classList.add('site-nav-entry--open');
        if (btn.nextElementSibling && btn.nextElementSibling.firstElementChild) {
          btn.nextElementSibling.style.height = `${btn.nextElementSibling.firstElementChild.clientHeight}px`;
        }
      } else {
        btn.parentElement.classList.remove('site-nav-entry--open');
        if (btn.nextElementSibling) btn.nextElementSibling.style.height = '';
      }
    });
  }

  _bindSearch() {
    const openBtn = this.querySelector('.search-trigger');
    if (openBtn) {
      openBtn.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.add('show-search');
        const field = document.querySelector('.main-search__input');
        if (field) setTimeout(() => field.focus(), 100);
      });
    }

    const closeBtn = document.querySelector('.main-search__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
      });
    }
  }
}

if (!customElements.get('store-header')) {
  customElements.define('store-header', StoreHeader);
}

/* Global listeners */
document.addEventListener('DOMContentLoaded', () => {
  /* Hamburger toggle */
  theme.addDelegateEventListener(document, 'click', '.hamburger-btn', (evt) => {
    evt.preventDefault();
    if (document.body.classList.contains('enable-mobile-nav-transition')) {
      theme.closeMobileNav();
    } else {
      theme.openMobileNav();
    }
  });

  /* Page shade */
  const pageBg = document.querySelector('.page-shade');
  if (pageBg) {
    pageBg.addEventListener('click', (evt) => {
      evt.preventDefault();
      document.body.classList.remove('reveal-mobile-nav', 'show-search');
      setTimeout(() => document.body.classList.remove('enable-mobile-nav-transition'), 750);
    });
  }

  /* Header overlay */
  const headerBg = document.querySelector('.header-overlay');
  if (headerBg) {
    headerBg.addEventListener('click', (evt) => {
      evt.preventDefault();
      document.body.classList.remove('reveal-mobile-nav', 'show-search');
      setTimeout(() => document.body.classList.remove('enable-mobile-nav-transition'), 750);
    });
  }

  /* Escape key */
  document.addEventListener('keydown', (evt) => {
    if (evt.key === 'Escape') {
      if (document.body.classList.contains('reveal-mobile-nav')) theme.closeMobileNav();
      if (document.body.classList.contains('show-search')) document.body.classList.remove('show-search');
    }
  });
});
